"use client";

import { HomeTopAnnouncementConfig } from "@/store/configStore";
import classNames from "classnames";
import Image from "next/image";
import { useRouter } from "next/navigation";
import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import { AnnouncementBg } from "./AnnouncementBg";
import IconArrow from "./assets/button-click-icon.svg";
import ImgLeft from "./assets/default-left.svg";
import imgRight from "./assets/default-right.png";
import styles from "./index.module.css";

interface DefaultAnnouncementProps {
  config: HomeTopAnnouncementConfig;
}

export const DefaultAnnouncement: React.FC<DefaultAnnouncementProps> = ({
  config,
}) => {
  const router = useRouter();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const checkVisibility = () => {
      const now = Date.now();
      const { startTime, endTime } = config;

      if (now >= startTime && now <= endTime) {
        setIsVisible(true);
      } else {
        setIsVisible(false);
      }
    };

    checkVisibility();
    const timerId = setInterval(checkVisibility, 1000);

    // Prefetch link if it's internal
    if (config.link?.startsWith("vidmuse:")) {
      const path = config.link.replace("vidmuse:", "");
      router.prefetch(path);
    }

    return () => clearInterval(timerId);
  }, [config]);

  if (!isVisible) {
    return null;
  }

  const handleClick = () => {
    if (!config.link) return;

    if (config.link.startsWith("vidmuse:")) {
      const path = config.link.replace("vidmuse:", "");
      router.push(path);
    } else if (config.link.startsWith("http")) {
      window.open(config.link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <div
      className={classNames(
        "relative w-full overflow-hidden transition-all duration-500 ease-in-out",
        "h-12 flex items-center justify-center cursor-pointer",
        styles.announcement,
      )}
      onClick={handleClick}
    >
      {/* Background Layer */}
      <AnnouncementBg className="absolute inset-0 w-full h-full" />

      {/* Decorative Assets */}
      <div
        className={classNames(
          "absolute left-0 top-0 h-full hidden md:block",
          styles.assetLeft,
        )}
      >
        <ImgLeft className="h-full w-auto object-contain" />
      </div>

      <div
        className={classNames(
          "absolute right-0 top-0 h-full hidden md:block",
          styles.assetRight,
        )}
      >
        <Image src={imgRight} alt="" className="h-full w-auto object-contain" />
      </div>

      <div className="relative z-1 w-full md:container md:mx-auto flex items-center justify-center gap-2 md:gap-6 overflow-hidden text-center">
        {/* Content with Marquee on Mobile */}
        <div className={styles.marqueeContainer}>
          <div
            className={classNames(
              styles.marqueeContent,
              "text-sm font-extrabold text-white whitespace-nowrap flex items-center",
            )}
          >
            <ReactMarkdown
              components={{
                p: ({ children }) => <>{children}</>,
                img: ({ ...props }) => (
                  <img
                    {...props}
                    className="max-h-[24px] w-auto inline-block align-middle"
                    alt={props.alt || ""}
                  />
                ),
              }}
            >
              {config.content}
            </ReactMarkdown>
          </div>
        </div>

        {config.button && (
          <div className="flex shrink-0 items-center gap-1 px-2 py-1 rounded-[6px] bg-white text-[#202020] text-xs font-semibold transition-transform hover:text-[#202020]/60 mx-2 md:mx-0">
            {config.button}
            <IconArrow className="inline-block w-3 h-3" />
          </div>
        )}
      </div>
    </div>
  );
};
